import { ActionTree } from 'vuex'
import { CONTEXT_TYPES } from '@/store/mutation-types'
import { RootState } from 'types'
import { ContextState } from './contextTypes'
import { addTranslations } from '@/locales'
import { transformObjectToQuery, missingMandatoryData, scrollTo } from '@/utils/helpers'
import { mapFromDidUserData, mapToDidUserData } from '@/utils/didHelpers'
import { DidAuthObject } from '@/utils/miscTypes'
import GetIdentifyData from '@/utils/identifyFields/countries'
import { Api } from '@/service/Service'
import JavascriptAPI from '@/utils/JavascriptAPI'

export const actions: ActionTree<ContextState, RootState> = {
  async setupApplication ({ dispatch }, config): Promise<any> {
    dispatch('setConfig', config)
    dispatch('getTranslations')
    dispatch('setInitRememberMeSettings')
  },

  setConfig ({ commit, dispatch }, config): any {
    commit(CONTEXT_TYPES.SET_CONFIG, config)
    const html = document.getElementsByTagName('html')[0]
    const containerWidthValue = (typeof config.containerWidth === 'string' && config.containerWidth.includes('px')) ? config.containerWidth : config.containerWidth + 'px'
    html.style.setProperty(`--containerWidth`, containerWidthValue)

    if (!config.didClientId) {
      return dispatch('setError', { key: 'main', error: 'missing_did_client_id' })
    }
  },

  async getTranslations ({ dispatch, getters }): Promise<any> {
    const { merchantId, locale } = getters.config
    if (!merchantId || !locale) {
      dispatch('setError', { key: 'main', error: 'failed_to_get_translations' })
      console.error(`Missing required value. merchantId: ${merchantId}, locale: ${locale}`)
      return
    }
    const { data } = await Api.getTranslations(locale, merchantId)
    await addTranslations(locale, data)
  },

  setI18nMessages ({ commit }, messages): any {
    commit(CONTEXT_TYPES.SET_I18N_MESSAGES, messages)
  },

  setInitRememberMeSettings ({ dispatch, getters }): any {
    const targetCountry = getters.config.country
    const countryConfig = GetIdentifyData[targetCountry]().settings
    let rememberDeviceSetting = countryConfig.rememberDevice
    let storeDataSetting = countryConfig.storeData

    const localRememberDevice = JSON.parse(localStorage.getItem(`piq_${targetCountry}_rememberDevice`))
    if (localRememberDevice !== null) {
      rememberDeviceSetting = localRememberDevice
    }

    const localStoreData = JSON.parse(localStorage.getItem(`piq_${targetCountry}_storeData`))
    if (localStoreData !== null) {
      storeDataSetting = localStoreData
    }

    // set init store value based on country if needed
    if (getters.settings.rememberDevice !== rememberDeviceSetting) {
      dispatch('toggleRememberDevice')
    }
    if (getters.settings.storeData !== storeDataSetting) {
      dispatch('toggleStoreData')
    }

    // set to localStorage
    localStorage.setItem(`piq_${targetCountry}_rememberDevice`, rememberDeviceSetting)
    localStorage.setItem(`piq_${targetCountry}_storeData`, storeDataSetting)
  },

  resetErrors ({ commit }) {
    commit(CONTEXT_TYPES.RESET_ERRORS)
  },

  setError ({ commit }, error: object | null): any {
    commit(CONTEXT_TYPES.SET_ERROR, error)
  },

  findExistingUserByDeviceId ({ dispatch, getters }, source): any {
    const checkUserDevice = getters.config.checkUserDevice
    const deviceId = localStorage.getItem('piq_dId')
    if (deviceId && checkUserDevice) {
      dispatch('checkDeviceId', { source, deviceId })
    } else {
      dispatch('setUserDeviceExists', false)
      dispatch('setInitIsLoading', false)
    }
  },

  // setDidCodeVerifierChallenge ({ commit }, { codeVerifier, codeChallenge }): any {
  setDidInitialAuth ({ commit }, { codeChallenge, codeVerifier }): any {
    commit(CONTEXT_TYPES.SET_DID_INITIAL_AUTH, { codeChallenge, codeVerifier })
  },

  setDidAuthTokens ({ commit, dispatch }, authTokens: DidAuthObject): any {
    // reset the initial verifiying code + challenge - not needed anymore when we have the tokens
    dispatch('setDidInitialAuth', { codeVerifier: null, codeChallenge: null })
    sessionStorage.setItem('didAccessToken', JSON.stringify({
      token: authTokens.access_token,
      lastUsed: new Date()
    }))
    commit(CONTEXT_TYPES.SET_DID_AUTH_TOKENS, authTokens)
    dispatch('doDidUserLookup')
  },

  resetDidAuthToken ({ commit }): any {
    sessionStorage.removeItem('didAccessToken') // avoid allowing the new user to cause manual login to edit the user tied to this didAccessToken
    commit(CONTEXT_TYPES.SET_DID_AUTH_TOKENS, { access_token: null, id_token: null })
  },

  async doDidUserLookup ({ commit, dispatch, getters }) {
    try {
      const { didAccessToken } = getters.context
      const { data } = await Api.getDidUserLookup(didAccessToken)

      const mappedData = mapFromDidUserData(data)
      dispatch('setUserData', mappedData)

      // BankId-SE doesn't provide email for example, which we should require?
      if (missingMandatoryData(mappedData)) {
        dispatch('setEditUser', true)
      } else {
        // don't render payment methods until we're done with user identification
        if (getters.config.identifyProvider === 'manual') {
          // Manual register use the same component as when editing a user
          // So when a manual register is done, we need to set editmode to false and clear the editUserData (we already have set in the identified user)
          dispatch('setEditUser', false)
          dispatch('resetEditUserData')
        } else {
          const userInfo: any = {}
          if (mappedData.nationalId) {
            userInfo.nationalId = mappedData.nationalId
          }
          JavascriptAPI.__CALLBACK_CUSTOMER_IDENTIFIED__(mappedData.userId, userInfo, {}, didAccessToken)
          scrollTo(0)
        }
      }

      dispatch('setWaitingForIdentifyProvider', false)
      dispatch('setIsLoading', false)
    } catch (err) {
      dispatch('setWaitingForIdentifyProvider', false)
      dispatch('setIsLoading', false)
      dispatch('setError', { key: 'main', error: 'failed_to_get_user_info' })
      console.error(err)
      console.error('Failed to fetch user information from DID')
      dispatch('resetDidAuthToken')
    } finally {
      dispatch('setInitIsLoading', false)
    }
  },

  setUserWizardData ({ commit }, payload): void {
    commit(CONTEXT_TYPES.SET_USER_WIZARD_DATA, payload)
  },

  setIdentifyProviderId ({ commit }, providerId): void {
    commit(CONTEXT_TYPES.SET_IDENTIFY_PROVIDER, providerId)
  },

  setIdentifyProvider ({ commit }, provider): void {
    commit(CONTEXT_TYPES.SET_WIZARD_PROVIDER_OBJECT, provider)
    setTimeout(() => { JavascriptAPI.__REPORT_CLIENT_HEIGHT__() }, 50)
  },

  setUserData ({ commit }, payload): void {
    commit(CONTEXT_TYPES.SET_USER_DATA, payload)
  },

  setEditUserData ({ commit }, payload): void {
    commit(CONTEXT_TYPES.SET_EDIT_USER_DATA, payload)
  },

  resetEditUserData ({ commit }, payload): void {
    commit(CONTEXT_TYPES.RESET_EDIT_USER_DATA)
  },

  continueNextStep ({ dispatch }) {
    dispatch('setIdentifyWizardStep')
  },

  setInitIsLoading ({ commit }, isLoading: boolean) {
    commit(CONTEXT_TYPES.SET_INIT_IS_LOADING, isLoading)
  },

  setIsLoading ({ commit }, isLoading: boolean) {
    commit(CONTEXT_TYPES.SET_IS_LOADING, isLoading)
  },

  setEditUser ({ commit, getters }, editUser: boolean) {
    commit(CONTEXT_TYPES.SET_EDIT_USER, editUser)
    if (editUser) {
      setTimeout(() => { JavascriptAPI.__CALLBACK_CUSTOMER_EDIT__() }, 50)
    } else if (!editUser && getters.identifiedUser) {
      const { identifiedUser, didAccessToken } = getters

      const userInfo: any = {}
      if (identifiedUser.nationalId) {
        userInfo.nationalId = identifiedUser.nationalId
      }
      JavascriptAPI.__CALLBACK_CUSTOMER_IDENTIFIED__(identifiedUser.userId, userInfo, {}, didAccessToken)
      scrollTo(0)
    }
  },

  validateUser ({ dispatch, commit, getters }) {
    setTimeout(() => { JavascriptAPI.__REPORT_CLIENT_HEIGHT__() }, 50)

    // reset before running a new validate
    dispatch('setEditUserErrors', {})

    const { editUserData, didAccessToken, config } = getters
    const fields: Array<any> = GetIdentifyData[config.country]().fields
    let isValid: boolean = true
    const errorFields: object = {}
    fields.forEach(field => {
      const fieldId = field.id
      const fieldValue = editUserData[fieldId]
      if (field.required && !field.validateMethod(fieldValue)) {
        errorFields[field.id] = field
        isValid = false
      }
    })
    if (isValid) {
      if (didAccessToken) {
        // when we are dealing with an existing user being edited
        dispatch('saveEditUser')
      } else {
        // when a user is being registered manually
        dispatch('setIsLoading', true)
        dispatch('setTriggerUserManualRegister', true)
      }
    } else {
      dispatch('setEditUserErrors', errorFields)
    }
  },

  setTriggerUserManualRegister ({ commit }, shouldTrigger) {
    commit(CONTEXT_TYPES.TRIGGER_USER_MANUAL_REGISTER, shouldTrigger)
  },

  setEditUserErrors ({ commit }, errorFields) {
    commit(CONTEXT_TYPES.SET_EDIT_USER_ERRORS, errorFields)
  },

  async saveEditUser ({ dispatch, getters }) {
    dispatch('setIsLoading', true)
    const { editUserData, didAccessToken } = getters
    if (!didAccessToken) {
      dispatch('setError', { key: 'main', error: 'missing_did_access_token' })
      dispatch('setIsLoading', false)
    }

    try {
      const didData = mapToDidUserData(editUserData)
      const { data } = await Api.updateDidUser(didAccessToken, didData)
      dispatch('setUserData', editUserData)
      dispatch('setEditUser', false)
      dispatch('setIsLoading', false)

      // const userInfo: any = {}
      // if (editUserData.nationalId) {
      //   userInfo.nationalId = editUserData.nationalId
      // }
      // JavascriptAPI.__CALLBACK_CUSTOMER_IDENTIFIED__(editUserData.userId, userInfo, {}, didAccessToken)
      scrollTo(0)
    } catch (error) {
      console.error(error)
      dispatch('setIsLoading', false)
    }
  },

  setIdentifyWizardStep ({ commit, dispatch, getters }, step: number | undefined): void {
    const { config, identifyWizardStep } = getters
    let nextStep = null
    if (step !== undefined) {
      nextStep = step
    } else {
      nextStep = identifyWizardStep + 1
    }
    if (nextStep === config.identifyFields.length) {
      dispatch('setEditUser', true)
      dispatch('setIdentifyProviderId', 'manual')
    } else {
      commit(CONTEXT_TYPES.SET_IDENTIFY_WIZARD_STEP, nextStep)
    }
    setTimeout(() => { JavascriptAPI.__REPORT_CLIENT_HEIGHT__() }, 50)
  },

  setWizardFields ({ commit }, fields: Array<string>): void {
    commit(CONTEXT_TYPES.SET_WIZARD_FIELDS, fields)
  },

  async checkDeviceId ({ dispatch, getters }, { deviceId }): Promise<any> {
    try {
      const { data } = await Api.checkDeviceId(deviceId)
      dispatch('setUserDeviceExists', data.found)
    } catch (err) {
      if (!getters.config.didClientId) {
        dispatch('setError', { key: 'main', error: 'missing_did_client_id' })
      } else {
        dispatch('setError', { key: 'main', error: 'failed_to_check_device' })
      }
      console.error(err)
    } finally {
      dispatch('setInitIsLoading', false)
    }
  },

  // Did we get a match on the deviceId we passed in?
  setUserDeviceExists ({ commit }, exists): any {
    commit(CONTEXT_TYPES.SET_USER_DEVICE_EXISTS, exists)
  },

  setCheckUserDevice ({ commit }, checkUserDevice: boolean): any {
    commit(CONTEXT_TYPES.SET_CHECK_USER_DEVICE, checkUserDevice)
  },

  /* When we get a match on deviceId - that we already have a user with that deviceId
     In that case we only require another matching field (ssn, email, zip for example)
  */
  async lookupExistingDidUser ({ dispatch, getters }, { fieldIndex, deviceId = null, initialSource }): Promise<any> {
    try {
      const source = getters.config.source || initialSource
      const userWizardData = transformObjectToQuery(getters.userWizardData)
      const didAccessToken = getters.didAccessToken
      const response = await Api.lookupExistingDidUser(source, 'device', deviceId, userWizardData, 'SE')
      const data = response.data

      // Found a user
      if (data.user) {
        dispatch('setUserData', data.user)
        const userData: any = {}
        if (data.nationalId) {
          userData.nationalId = data.nationalId
        }
        JavascriptAPI.__CALLBACK_CUSTOMER_IDENTIFIED__(data.userId, userData, {}, didAccessToken)
        dispatch('setWaitingForIdentifyProvider', false)
      } else if (fieldIndex) {
        dispatch('setIdentifyWizardStep', fieldIndex)
      }
      dispatch('setIsLoading', false)
    } catch (err) {
      dispatch('setError', { key: 'main', error: 'failed_to_find_existing_user' })
      dispatch('setWaitingForIdentifyProvider', false)
      dispatch('setIsLoading', false)
      throw err
    } finally {
      dispatch('setInitIsLoading', false)
    }
  },

  setWaitingForIdentifyProvider ({ commit }, isWaiting): any {
    commit(CONTEXT_TYPES.SET_WAITING_FOR_IDENTIFY_PROVIDER, isWaiting)
    setTimeout(() => { JavascriptAPI.__REPORT_CLIENT_HEIGHT__() }, 50)
  },

  setEditCustomShipping ({ commit }): any {
    commit(CONTEXT_TYPES.SET_EDIT_SHIPPING)
    setTimeout(() => { JavascriptAPI.__REPORT_CLIENT_HEIGHT__() }, 50)
  },

  setShippingData ({ commit, getters }): void {
    commit(CONTEXT_TYPES.SET_SHIPPING_DATA, getters.editShippingAddressData)
    setTimeout(() => { JavascriptAPI.__REPORT_CLIENT_HEIGHT__() }, 50)
  },

  setEditShippingData ({ commit }, payload): void {
    commit(CONTEXT_TYPES.SET_EDIT_SHIPPING_DATA, payload)
  },

  resetShippingAddress ({ commit }): void {
    commit(CONTEXT_TYPES.RESET_SHIPPING_DATA)
  },

  toggleRememberDevice ({ commit }): any {
    commit(CONTEXT_TYPES.TOGGLE_REMEMBER_DEVICE)
  },
  toggleStoreData ({ commit }): any {
    commit(CONTEXT_TYPES.TOGGLE_STORE_DATA)
  },

  resetIdentifiedUser ({ commit, getters }): any {
    const { identifyProvider } = getters.config
    commit(CONTEXT_TYPES.RESET_IDENTIFIED_USER, identifyProvider)
    JavascriptAPI.__CALLBACK_CUSTOMER_RESET__()
  }
}
